import React from "react";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import "react-calendar/dist/Calendar.css";
import PasswordValidation from "../elements/PasswordValidation";
import siteData from "../data/Site_List_For_GiveStar.json";

// Site types and their corresponding keys in the JSON + field mappings
const SITE_TYPE_CONFIG = {
  "Premier Inn": {
    dataKey: "Premier Inns",
    nameField: "PREMIER INN NAME",
    emailField: "PREMIER INN EMAIL ADDRESS",
    oracleField: "PREMIER INN ORACLE NUMBER"
  },
  "Restaurant": {
    dataKey: "Restaurants",
    nameField: "RESTAURANT HOUSE NAME",
    emailField: "EMAIL ADDRESS FOR RESTAURANT",
    oracleField: "RESTAURANT ORACLE NUMBER"
  },
  "Support Centre": {
    dataKey: "Support Centre",
    nameField: "Unnamed: 0",
    emailField: null,
    oracleField: null
  }
};

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "white",
    border: "1px solid #e6e6e6",
    borderRadius: "4px",
    fontSize: "16px",
    minHeight: "38px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#e6e6e6"
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: "#000000",
    fontSize: "16px"
  }),
  singleValue: (base) => ({
    ...base,
    color: "#000000",
    fontSize: "16px"
  }),
  input: (base) => ({
    ...base,
    color: "#000000",
    fontSize: "16px"
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#09d7ff" : state.isFocused ? "#e6f9ff" : "white",
    color: "#000000",
    fontSize: "16px",
    "&:active": {
      backgroundColor: "#09d7ff"
    }
  })
};

function RegistrationForm({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  selectedSite,
  setSelectedSite,
  selectedSiteType,
  setSelectedSiteType,
  email,
  setEmail,
  isEmpty,
  checkbox1,
  checkbox2,
  setCheckbox1,
  setCheckbox2,
  password,
  setPassword,
  checkPassword,
  specialChar,
  lowcase,
  uppercase,
  hasNumber,
  charsNumber,
  setSelectedSiteFull,
}) {
  const [showSiteTypeHint, setShowSiteTypeHint] = React.useState(false);
  // Get sites for selected type and convert to react-select options
  const siteOptions = React.useMemo(() => {
    if (!selectedSiteType) return [];

    const config = SITE_TYPE_CONFIG[selectedSiteType];
    const sites = siteData[config.dataKey] || [];

    return sites
      .map(site => {
        const name = site[config.nameField]?.trim();
        if (!name) return null;

        return {
          value: name,
          label: name,
          data: {
            site_name: name,
            oracle_number: site[config.oracleField] || null,
            email: site[config.emailField] || null,
            region: site.REGION || null,
            division: site.Division || site.DIVISION || null,
            concept: site.CONCEPT || null,
            site_type: selectedSiteType
          }
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedSiteType]);
  return (
    <div className="registration-form">
      <div>
        <p className="titleMain">
          REGISTER TO FUNDRAISE FOR{" "}
          <span className="titleMain mb-2 desktopHide">GOSH CHARITY</span>
        </p>
        <p className="titleMain mb-2 mobileHide">GOSH CHARITY</p>
        <p className="text-body">
          Please sign up using your named Whitbread email address,
          rather than a personal email. Registering will create your account on the
          givestar app.
        </p>
      </div>

      <div className="mt-3 mb-2">
        <div className="is-flex justify-space-between nameFields">
          <div className="mb-2 inputWidth mr-2">
            <input
              className={`input ${
                isEmpty && firstName === "" ? "emptyInput" : ""
              }`}
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="mb-2 inputWidth">
            <input
              className={`input inputStyle ${
                isEmpty && lastName === "" ? "emptyInput" : ""
              }`}
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-2">
          <input
            className={`input inputStyle ${
              isEmpty && email === "" ? "emptyInput" : ""
            }`}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="mb-1 flexOne">
          <p className={`form-label ${showSiteTypeHint ? "hint-error" : ""}`}>
            SELECT SITE TYPE {showSiteTypeHint && <span className="hint-text">← Please select first</span>}
          </p>
          <div className={`is-flex site-type-buttons ${showSiteTypeHint ? "site-type-hint" : ""}`} style={{ gap: "10px" }}>
            {["Premier Inn", "Restaurant", "Support Centre"].map((type) => (
              <div
                key={type}
                className={`targetBtn ${selectedSiteType === type ? "targetBtnSelected" : ""}`}
                onClick={() => {
                  setSelectedSiteType(type);
                  setSelectedSite("");
                  setSelectedSiteFull({});
                  setShowSiteTypeHint(false);
                }}
              >
                <p className="has-text-weight-bold">{type}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-1 flexOne">
          <p className="form-label">SELECT SITE</p>
          <div onClick={() => { if (!selectedSiteType) setShowSiteTypeHint(true); }}>
            <Select
              value={selectedSite ? { value: selectedSite, label: selectedSite } : null}
              onChange={(option) => {
                if (option) {
                  setSelectedSite(option.value);
                  setSelectedSiteFull(option.data);
                } else {
                  setSelectedSite("");
                  setSelectedSiteFull({});
                }
              }}
              options={siteOptions}
              isDisabled={!selectedSiteType}
              isClearable
              isSearchable
              placeholder={selectedSiteType ? "Search for your site..." : "Select site type first"}
              styles={customSelectStyles}
              menuPortalTarget={document.body}
              maxMenuHeight={400}
              noOptionsMessage={() => "No sites found"}
            />
          </div>
        </div>

        <div className="mb-1">
          <input
            className={`input password-input ${
              isEmpty && password === "" ? "emptyInput" : ""
            }`}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              checkPassword(e.target.value);
            }}
          />
        </div>

        <PasswordValidation
          charsNumber={charsNumber}
          lowcase={lowcase}
          uppercase={uppercase}
          hasNumber={hasNumber}
          specialChar={specialChar}
        />

        <div>
          <div className="mb-1">
            <input
              type="checkbox"
              id="checkbox1"
              name="checkbox1"
              className="consent-checkbox"
              checked={checkbox1}
              onChange={() => setCheckbox1(!checkbox1)}
            />
            <label className="ml-2 termsTxt" htmlFor="checkbox1">
              I agree to givestar’s{" "}
              <a
                href="https://givestar.io/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
                className="termsTxtLink"
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="https://givestar.io/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="termsTxtLink"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          <div>
            <input
              type="checkbox"
              id="checkbox2"
              name="checkbox2"
              checked={checkbox2}
              className="consent-checkbox"
              onChange={() => setCheckbox2(!checkbox2)}
            />
            <label className="ml-2 termsTxt" htmlFor="checkbox2">
              I understand a summary of my fundraising page, including my name
              and the total funds raised, is shared with GOSH Charity. For more
              information on how GOSH Charity uses your information, please see{" "}
              <span>
                <a
                  href="https://gosh.org/privacy-policy-statement/"
                  target="_blank"
                  rel="noreferrer"
                  className="termsTxtLink ml-1"
                >
                  https://gosh.org/privacy-policy-statement/
                </a>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationForm;
